import React, { useState } from 'react';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';

// ============================================================
// AirlineReservations.tsx
//
// GCC-focused route gallery: real destination photography per
// route, plus IATA two-letter code badges standing in for airline
// "logos" — actual airline logo artwork is trademarked, so these
// are original circular monograms using each carrier's public
// IATA code (the same codes shown on any boarding pass), not a
// copy of their branding.
// ============================================================

interface Airline {
  code: string;
  name: string;
  note: string;
  color: string;
}

interface Route {
  id: string;
  city: string;
  country: string;
  duration: string;
  image?: string;
  codes: string[];
}

interface Faq {
  q: string;
  a: string;
}

const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1688751582902-914dbae7a69b?w=1800&q=80&fit=crop&crop=center',
  split: 'https://images.unsplash.com/photo-1690964099679-46adb512b5cf?w=1200&q=80&fit=crop&crop=center',
  muscat: 'https://images.unsplash.com/photo-1699855803432-17f4ab468b2d?w=900&q=80&fit=crop&crop=center',
  dubai: 'https://images.unsplash.com/photo-1735320860585-08578659aec3?w=900&q=80&fit=crop&crop=center',
  abudhabi: 'https://images.unsplash.com/photo-1661632350562-f8ad6a2665d9?w=900&q=80&fit=crop&crop=center',
  doha: 'https://images.unsplash.com/photo-1647755453118-aad22e0f7cc0?w=900&q=80&fit=crop&crop=center',
  manama: 'https://images.unsplash.com/photo-1643721505487-0e83def03e53?w=900&q=80&fit=crop&crop=center',
  riyadh: 'https://images.unsplash.com/photo-1699023654953-ce7fd12741fb?w=900&q=80&fit=crop&crop=center',
};

// Real, public IATA two-letter carrier codes — the same codes printed
// on every ticket and boarding pass. Colors below are an original UI
// palette for this site, not each airline's official brand color.
const airlines: Airline[] = [
  { code: 'WY', name: 'Oman Air', note: 'National carrier, our most-booked airline', color: '#b09a50' },
  { code: 'EK', name: 'Emirates', note: 'Dubai hub, wide long-haul network', color: '#a8384a' },
  { code: 'QR', name: 'Qatar Airways', note: 'Doha hub, strong Europe & Asia links', color: '#5c1f2e' },
  { code: 'EY', name: 'Etihad Airways', note: 'Abu Dhabi hub', color: '#294b5a' },
  { code: 'GF', name: 'Gulf Air', note: 'Bahrain hub, short Gulf hops', color: '#1a3140' },
  { code: 'FZ', name: 'flydubai', note: 'Budget-friendly regional connections', color: '#c2652a' },
  { code: 'G9', name: 'Air Arabia', note: 'Low-cost, Sharjah-based', color: '#b3392c' },
  { code: 'OV', name: 'SalamAir', note: "Oman's own low-cost carrier", color: '#3b8fa3' },
  { code: 'SV', name: 'Saudia', note: 'Direct into Riyadh & Jeddah', color: '#2f6b46' },
  { code: 'KU', name: 'Kuwait Airways', note: 'Direct into Kuwait City', color: '#3b6272' },
  { code: 'TK', name: 'Turkish Airlines', note: 'Istanbul stopovers to Europe & beyond', color: '#a8384a' },
  { code: 'BA', name: 'British Airways', note: 'Direct into London', color: '#1a3140' },
  { code: 'FR', name: 'Ryanair', note: 'Onward hops once you land in Europe', color: '#294b5a' },
];

const routes: Route[] = [
  { id: 'dubai', city: 'Dubai', country: 'United Arab Emirates', duration: '~1h', image: IMAGES.dubai, codes: ['WY', 'FZ', 'EK'] },
  { id: 'abudhabi', city: 'Abu Dhabi', country: 'United Arab Emirates', duration: '~1h', image: IMAGES.abudhabi, codes: ['WY', 'EY'] },
  { id: 'doha', city: 'Doha', country: 'Qatar', duration: '~1h05', image: IMAGES.doha, codes: ['WY', 'QR'] },
  { id: 'manama', city: 'Manama', country: 'Bahrain', duration: '~1h10', image: IMAGES.manama, codes: ['WY', 'GF'] },
  { id: 'riyadh', city: 'Riyadh / Jeddah', country: 'Saudi Arabia', duration: '~1h45–2h', image: IMAGES.riyadh, codes: ['WY', 'SV'] },
  { id: 'kuwait', city: 'Kuwait City', country: 'Kuwait', duration: '~1h40', codes: ['WY', 'KU'] },
];

const faqs: Faq[] = [
  { q: 'What happens if my flight is cancelled?', a: "We rebook you on the next available option and confirm it with you directly — you don't need to call the airline yourself." },
  { q: 'Can you book group flights?', a: 'Yes, group fares and seating are arranged together so your party stays on the same flights and, where possible, seated together.' },
  { q: 'Do I still earn frequent flyer miles?', a: "Yes — tickets are issued under your own frequent flyer number wherever the fare class allows it." },
  { q: 'Are the flight times shown exact?', a: "They're typical, rounded flight times to help you plan — actual schedules and durations vary by day and season, and we confirm the real timings when we quote your ticket." },
];

function DuneRule() {
  return (
    <div aria-hidden="true" style={{ lineHeight: 0 }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16" fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5" />
        <path d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22" fill="none" stroke="rgba(41,75,90,.14)" strokeWidth="1" />
      </svg>
    </div>
  );
}

function AirlineBadge({ code }: { code: string }) {
  const a = airlines.find((x) => x.code === code);
  if (!a) return null;
  return (
    <span className="al-badge" style={{ background: a.color }} title={a.name}>
      {a.code}
    </span>
  );
}

export default function AirlineReservations() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }
        * { box-sizing: border-box; }
        .visa-page { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        .visa-page h1, .visa-page h2, .visa-page h3 { font-family: 'Playfair Display', serif; }
        .visa-page a:focus-visible, .visa-page button:focus-visible { outline: 2px solid var(--gold-d); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { .visa-page *, .visa-page *::before, .visa-page *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }
        .eyebrow.centered { justify-content: center; }

        .section-title { font-size: clamp(1.7rem, 2.6vw, 2.4rem); font-weight: 500; color: var(--ink); line-height: 1.2; margin: 10px 0 12px; }
        .section-title.centered { text-align: center; }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 560px; }
        .section-sub.centered { margin: 0 auto; text-align: center; }
        .section-pad { padding: 84px 0; }
        @media (max-width: 991px) { .section-pad { padding: 64px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 48px 0; } }
        .bg-cream { background: var(--cream); }
        .bg-white { background: #fff; }
        .bg-teal { background: var(--teal-d); }

        .vbtn-primary { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--teal-d); border-radius: 5px; font-size: 12.5px; font-weight: 700; letter-spacing: .3px; padding: 14px 26px; text-decoration: none; transition: transform .2s, background .2s; box-shadow: 0 10px 28px rgba(211,190,118,.28); border: none; cursor: pointer; }
        .vbtn-primary:hover { background: #e2ce86; transform: translateY(-2px); }
        .vbtn-ghost { display: inline-flex; align-items: center; background: rgba(255,255,255,.1); backdrop-filter: blur(4px); color: #fff; border: 1px solid rgba(255,255,255,.25); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 14px 24px; text-decoration: none; transition: background .2s, border-color .2s; }
        .vbtn-ghost:hover { border-color: #fff; background: rgba(255,255,255,.18); }
        .vbtn-outline { display: inline-flex; align-items: center; justify-content: center; gap: 8px; background: transparent; color: var(--teal-d); border: 1.5px solid var(--gold); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 12px 22px; text-decoration: none; transition: background .2s, transform .2s; cursor: pointer; width: 100%; }
        .vbtn-outline:hover { background: var(--gold); transform: translateY(-2px); }

        /* HERO */
        .svc-hero { position: relative; min-height: 500px; display: flex; align-items: center; overflow: hidden; background: var(--teal-d); }
        .svc-hero-img { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: 0.36; }
        .svc-hero-veil { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(26,49,61,.5) 0%, rgba(26,49,61,.92) 100%); }
        .svc-hero-inner { position: relative; z-index: 2; padding: 64px 0 48px; }
        .svc-breadcrumb { font-size: 11.5px; color: rgba(255,255,255,.55); margin-bottom: 18px; }
        .svc-breadcrumb a { color: rgba(255,255,255,.75); text-decoration: none; }
        .svc-breadcrumb a:hover { color: var(--gold-l); }
        .svc-hero-title { font-weight: 600; color: #fff; line-height: 1.16; font-size: clamp(30px, 4vw, 48px); max-width: 680px; margin-bottom: 16px; }
        .svc-hero-title .gold { font-style: italic; color: var(--gold); font-weight: 400; }
        .svc-hero-sub { font-size: 15px; color: rgba(255,255,255,.78); font-weight: 300; line-height: 1.75; max-width: 560px; margin-bottom: 30px; }
        .svc-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }

        /* AIRLINE BADGE (IATA code monogram, not a logo) */
        .al-badge {
          display: inline-flex; align-items: center; justify-content: center;
          width: 30px; height: 30px; border-radius: 50%; color: #fff;
          font-size: 10.5px; font-weight: 700; letter-spacing: .2px;
          box-shadow: 0 3px 8px rgba(26,49,61,.25); border: 2px solid #fff;
          flex: none;
        }

        /* AIRLINES GRID */
        .airline-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
        @media (max-width: 900px) { .airline-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 620px) { .airline-grid { grid-template-columns: 1fr 1fr; } }
        .airline-card {
          background: #fff; border: 1px solid rgba(41,75,90,.09); border-radius: var(--radius);
          padding: 18px; display: flex; align-items: center; gap: 12px;
          transition: border-color .2s, box-shadow .2s, transform .2s;
        }
        .airline-card:hover { border-color: rgba(211,190,118,.55); box-shadow: var(--shadow-sm); transform: translateY(-3px); }
        .airline-card .al-badge { width: 38px; height: 38px; font-size: 12px; }
        .airline-card .name { font-family: 'Playfair Display', serif; font-weight: 600; font-size: .95rem; color: var(--ink); }
        .airline-card .note { font-size: 10.5px; color: var(--silver); font-weight: 300; margin-top: 3px; line-height: 1.4; }

        /* ROUTE GALLERY */
        .route-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
        @media (max-width: 900px) { .route-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 620px) { .route-grid { grid-template-columns: 1fr; } }
        .route-card {
          border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow-sm);
          border: 1px solid rgba(41,75,90,.08); background: #fff;
          transition: transform .3s, box-shadow .3s;
        }
        .route-card:hover { transform: translateY(-5px); box-shadow: var(--shadow-md); }
        .route-card-img { height: 170px; background-size: cover; background-position: center; }
        .route-card-img.placeholder {
          background: linear-gradient(135deg, var(--teal-d), var(--teal));
          display: flex; align-items: center; justify-content: center;
        }
        .route-card-img.placeholder span { color: rgba(255,255,255,.35); font-family: 'Playfair Display', serif; font-size: 2rem; font-weight: 600; letter-spacing: 2px; }
        .route-card-body { padding: 18px 20px 20px; }
        .route-card-body .city { font-family: 'Playfair Display', serif; font-size: 1.1rem; font-weight: 600; color: var(--ink); }
        .route-card-body .country { font-size: 11.5px; color: var(--gold-d); font-weight: 600; letter-spacing: .3px; display: block; margin: 2px 0 10px; }
        .route-card-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 12px; }
        .route-card-badges { display: flex; gap: -6px; }
        .route-card-badges .al-badge { margin-left: -8px; }
        .route-card-badges .al-badge:first-child { margin-left: 0; }
        .route-card-duration { font-size: 12px; color: var(--silver); font-weight: 400; }

        /* FEATURED SPLIT PANEL */
        .svc-split { display: grid; grid-template-columns: 1fr 1fr; border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow-md); border: 1px solid rgba(41,75,90,.09); background: #fff; }
        @media (max-width: 900px) { .svc-split { grid-template-columns: 1fr; } }
        .svc-split-media { position: relative; min-height: 320px; background-size: cover; background-position: center; }
        @media (min-width: 901px) { .svc-split-media { min-height: 100%; } }
        .svc-split-content { padding: 44px 42px; display: flex; flex-direction: column; justify-content: center; }
        .svc-split-title { font-size: clamp(1.4rem, 2vw, 1.9rem); font-weight: 600; color: var(--ink); line-height: 1.2; margin: 10px 0 14px; }
        .svc-split-text { font-size: 14.5px; color: var(--silver); font-weight: 300; line-height: 1.75; margin-bottom: 22px; }
        .split-list { list-style: none; padding: 0; margin: 0; }
        .split-list li { font-size: 13px; color: var(--teal); font-weight: 400; padding: 5px 0; display: flex; align-items: center; gap: 9px; }
        .split-list li::before { content: '✓'; color: var(--gold-d); font-weight: 700; }

        /* FAQ */
        .faq-list { max-width: 760px; margin: 0 auto; }
        .faq-item { border-bottom: 1px solid rgba(41,75,90,.1); }
        .faq-item:first-child { border-top: 1px solid rgba(41,75,90,.1); }
        .faq-q {
          width: 100%; background: none; border: none; text-align: left; cursor: pointer;
          display: flex; align-items: center; justify-content: space-between; gap: 20px;
          padding: 22px 4px; font-family: 'Playfair Display', serif; font-size: 1.02rem; font-weight: 500; color: var(--ink);
        }
        .faq-q .plus { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid var(--gold-d); position: relative; transition: transform .3s ease, background .3s ease; }
        .faq-q .plus::before, .faq-q .plus::after { content: ''; position: absolute; background: var(--gold-d); top: 50%; left: 50%; transform: translate(-50%,-50%); }
        .faq-q .plus::before { width: 9px; height: 1.4px; }
        .faq-q .plus::after { width: 1.4px; height: 9px; transition: opacity .2s ease; }
        .faq-item.open .plus { background: var(--gold-d); transform: rotate(180deg); }
        .faq-item.open .plus::before, .faq-item.open .plus::after { background: #fff; }
        .faq-item.open .plus::after { opacity: 0; }
        .faq-a { max-height: 0; overflow: hidden; transition: max-height .35s ease; }
        .faq-item.open .faq-a { max-height: 240px; }
        .faq-a p { font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.75; padding: 0 4px 24px; max-width: 640px; }

        /* CTA BANNER */
        .cta-banner { background: var(--teal); border-radius: var(--radius); padding: 44px 40px; display: flex; align-items: center; justify-content: space-between; gap: 30px; flex-wrap: wrap; }
        .cta-banner-title { color: #fff; font-size: 1.3rem; font-weight: 500; margin-bottom: 4px; }
        .cta-banner-sub { color: rgba(255,255,255,.7); font-size: 13px; font-weight: 300; max-width: 460px; }

        @media (max-width: 576px) { .cta-banner { padding: 28px 20px; } }
`}</style>

      <div className="visa-page">
        <Header />

        {/* ===== HERO ===== */}
        <section className="svc-hero">
          <div className="svc-hero-img" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
          <div className="svc-hero-veil" />
          <div className="container svc-hero-inner">
            <div className="svc-breadcrumb"><a href="/services">Services</a> / Airline Reservations</div>
            <div className="eyebrow light" style={{ marginBottom: 14 }}>
              Flights, Sorted by People Who Fly Them Too
            </div>
            <h1 className="svc-hero-title">
              Airline Reservations, <span className="gold">Made Simple</span>
            </h1>
            <p className="svc-hero-sub">
              Whether it's a quick hop across the Gulf or a long-haul to London, we book across
              the airlines that actually serve the region — and stay on the case if anything
              changes between now and your departure gate.
            </p>
            <div className="svc-hero-ctas">
              <a href="#routes" className="vbtn-primary">See GCC Routes</a>
              <a href="/contact" className="vbtn-ghost">Get a Quote</a>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== ROUTES ===== */}
        <section className="section-pad bg-white" id="routes">
          <div className="container">
            <div style={{ marginBottom: 40 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                From Muscat, Around the Gulf
              </span>
              <h2 className="section-title centered">GCC Routes We Book Most</h2>
              <p className="section-sub centered">
                Flight times are typical and rounded — we confirm the exact schedule, fare, and
                airline availability when you enquire.
              </p>
            </div>
            <div className="route-grid">
              {routes.map((r) => (
                <div className="route-card" key={r.id}>
                  {r.image ? (
                    <div className="route-card-img" style={{ backgroundImage: `url(${r.image})` }} />
                  ) : (
                    <div className="route-card-img placeholder"><span>{r.city.slice(0, 3).toUpperCase()}</span></div>
                  )}
                  <div className="route-card-body">
                    <div className="city">Muscat → {r.city}</div>
                    <span className="country">{r.country}</span>
                    <div className="route-card-footer">
                      <div className="route-card-badges">
                        {r.codes.map((c) => <AirlineBadge key={c} code={c} />)}
                      </div>
                      <span className="route-card-duration">{r.duration}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== AIRLINES ===== */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div style={{ marginBottom: 40 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Who We Book With
              </span>
              <h2 className="section-title centered">Real Airlines, Booked Directly</h2>
              <p className="section-sub centered">
                No mystery consolidator fares — every ticket is issued with a named carrier you
                can look up, track, and check in with yourself. Codes shown are each airline's
                public IATA code, same as on your boarding pass.
              </p>
            </div>
            <div className="airline-grid">
              {airlines.map((a) => (
                <div className="airline-card" key={a.code}>
                  <span className="al-badge" style={{ background: a.color }}>{a.code}</span>
                  <div>
                    <div className="name">{a.name}</div>
                    <div className="note">{a.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== SPLIT PANEL ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div className="svc-split">
              <div className="svc-split-media" style={{ backgroundImage: `url(${IMAGES.split})` }} />
              <div className="svc-split-content">
                <span className="eyebrow">The Details</span>
                <div className="svc-split-title">One ticket, fully looked after</div>
                <p className="svc-split-text">
                  Booking a flight is easy. Keeping it correct through schedule changes, seat
                  requests, and last-minute rebookings is the part that takes an actual travel
                  desk. That's what we handle, from the moment you ask to the moment you land.
                </p>
                <ul className="split-list">
                  <li>Seat & meal preference requests</li>
                  <li>E-ticket and itinerary sent instantly</li>
                  <li>Fare rules explained before you pay</li>
                  <li>Rebooking support if plans change</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== FAQ ===== */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div style={{ marginBottom: 36 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Before You Book
              </span>
              <h2 className="section-title centered">Frequently Asked Questions</h2>
            </div>
            <div className="faq-list">
              {faqs.map((f, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div className={`faq-item${isOpen ? ' open' : ''}`} key={f.q}>
                    <button
                      type="button"
                      className="faq-q"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                    >
                      <span>{f.q}</span>
                      <span className="plus" />
                    </button>
                    <div className="faq-a">
                      <p>{f.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== FINAL CTA ===== */}
        <section className="section-pad bg-white" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="cta-banner">
              <div>
                <div className="cta-banner-title">Get a Fare Comparison Today</div>
                <div className="cta-banner-sub">
                  Send us your route and dates — we'll come back with real options across real
                  airlines, not just one price.
                </div>
              </div>
              <a href="/contact" className="vbtn-primary">
                Get a Quote
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        <DefaultFooter />
      </div>
    </>
  );
}